import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Search, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { FilterSelect } from '../findings/filters/FilterSelect';
import { 
  CredentialsApi, 
  DomainName, 
  ServiceName, 
  StealerLeaksListOwnerEnum,
  TwoFaStatusEnum,
  StealerLeakSeverityEnum,
  ValidityEnum,
  Status111Enum
} from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';

export const ownerOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: StealerLeaksListOwnerEnum.Employee, label: 'Employee', color: 'text-blue-500' },
  { value: StealerLeaksListOwnerEnum.Customer, label: 'Customer', color: 'text-green-500' },
];

export const mobileOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: 'true', label: 'Mobile', color: 'text-purple-500' },
  { value: 'false', label: 'Desktop', color: 'text-orange-500' },
];

export const twoFaStatusOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: TwoFaStatusEnum.Enabled, label: 'Enabled', color: 'text-green-500' },
  { value: TwoFaStatusEnum.Disabled, label: 'Disabled', color: 'text-red-500' },
  { value: TwoFaStatusEnum.None, label: 'None', color: 'text-yellow-500' },
];

export const severityOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: StealerLeakSeverityEnum.Critical, label: 'Critical', color: 'text-red-700' },
  { value: StealerLeakSeverityEnum.High, label: 'High', color: 'text-red-500' },
  { value: StealerLeakSeverityEnum.Medium, label: 'Medium', color: 'text-yellow-500' },
  { value: StealerLeakSeverityEnum.Low, label: 'Low', color: 'text-blue-500' },
  { value: StealerLeakSeverityEnum.Info, label: 'Info', color: 'text-gray-400' },
];

export const validityOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: ValidityEnum.New, label: 'New', color: 'text-blue-500' },
  { value: ValidityEnum.Valid, label: 'Valid', color: 'text-green-500' },
  { value: ValidityEnum.Invalid, label: 'Invalid', color: 'text-red-500' },
];

export const statusOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: Status111Enum.Open, label: 'Open', color: 'text-blue-500' },
  { value: Status111Enum.InProgress, label: 'In Progress', color: 'text-yellow-500' },
  { value: Status111Enum.Resolved, label: 'Resolved', color: 'text-green-500' },
  { value: Status111Enum.FalsePositive, label: 'False Positive', color: 'text-purple-500' },
  { value: Status111Enum.Duplicate, label: 'Duplicate', color: 'text-gray-400' },
  { value: Status111Enum.Retest, label: 'Retest', color: 'text-orange-500' },
];

export const readOptions = [
  { value: 'all', label: 'All', color: 'text-gray-500' },
  { value: 'true', label: 'Seen', color: 'text-green-500' },
  { value: 'false', label: 'Unseen', color: 'text-red-500' },
];

interface StealerLeaksFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedDomain: string;
  onDomainChange: (domain: string) => void;
  selectedService: string;
  onServiceChange: (service: string) => void;
  selectedOwner: string;
  onOwnerChange: (owner: string) => void;
  isMobile: string;
  onMobileChange: (isMobile: string) => void;
  twoFaStatus: string;
  onTwoFaStatusChange: (status: string) => void;
  severity: string;
  onSeverityChange: (severity: string) => void;
  validity: string;
  onValidityChange: (validity: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
  read: string;
  onReadChange: (read: string) => void;
  onReset: () => void;
}

export const StealerLeaksFilters: React.FC<StealerLeaksFiltersProps> = ({
  searchQuery,
  onSearchChange,
  selectedDomain,
  onDomainChange,
  selectedService,
  onServiceChange,
  selectedOwner,
  onOwnerChange,
  isMobile,
  onMobileChange,
  twoFaStatus,
  onTwoFaStatusChange,
  severity,
  onSeverityChange,
  validity,
  onValidityChange,
  status,
  onStatusChange,
  read,
  onReadChange,
  onReset,
}) => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [domains, setDomains] = useState<{ value: string; label: string }[]>([]);
  const [services, setServices] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDomains = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.domainsList2({
        teamSlug: currentTeam.slug,
        pageSize: 1000,
      });

      const domainOptions = response.results
        .filter(domain => domain.name && domain.name.trim() !== '')
        .map((domain: DomainName) => ({
          value: domain.name,
          label: domain.name,
        }));
      setDomains([{ value: 'all', label: 'All Domains' }, ...domainOptions]);
    } catch (err) {
      console.error('Error fetching domains:', err);
    }
  }, [currentTeam?.slug, token]);

  const fetchServices = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.servicesList({
        teamSlug: currentTeam.slug,
        pageSize: 1000,
      });

      const serviceOptions = response.results
        .filter(service => service.name && service.name.trim() !== '')
        .map((service: ServiceName) => ({
          value: service.name,
          label: service.name,
        }));
      setServices([{ value: 'all', label: 'All Services' }, ...serviceOptions]);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  }, [currentTeam?.slug, token]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchDomains(), fetchServices()]).finally(() => {
      setIsLoading(false);
    });
  }, [fetchDomains, fetchServices]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 flex-wrap items-center">
        {/* Search */}
        <div className="flex-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Search stealer leaks..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>
        </div>

        {/* Quick Filters */}
        {/* <div className="flex flex-wrap items-center gap-4"> */}
          <div className="w-48 lg:w-40 md:w-56">
            <FilterSelect
              value={selectedDomain}
              onValueChange={onDomainChange}
              options={domains}
              placeholder="All Domains"
              label="Domain"
              scrollable={true}
              searchable={true}
            />
          </div>

          <div className="w-48 lg:w-40 md:w-56">
            <FilterSelect
              value={selectedService}
              onValueChange={onServiceChange}
              options={services}
              placeholder="All Services"
              label="Service"
              scrollable={true}
              searchable={true}
            />
          </div>

          <div className="w-32">
            <FilterSelect
              value={selectedOwner}
              onValueChange={onOwnerChange}
              options={ownerOptions}
              placeholder="All Owners"
              label="Owner"
            />
          </div>

          {/* <div className="w-32">
            <FilterSelect
              value={isMobile}
              onValueChange={onMobileChange}
              options={mobileOptions}
              placeholder="All Devices"
              label="Device Type"
            />
          </div> */}

          <div className="w-32">
            <FilterSelect
              value={twoFaStatus}
              onValueChange={onTwoFaStatusChange}
              options={twoFaStatusOptions}
              placeholder="All 2FA"
              label="2FA Status"
            />
          </div>

          <div className="w-40">
            <FilterSelect
              value={severity}
              onValueChange={onSeverityChange}
              options={severityOptions}
              placeholder="All Severities"
              label="Severity"
            />
          </div>

          <div className="w-25">
            <FilterSelect
              value={validity}
              onValueChange={onValidityChange}
              options={validityOptions}
              placeholder="Validity"
              label="Validity"
            />
          </div>

          <div className="w-25">
            <FilterSelect
              value={status}
              onValueChange={onStatusChange}
              options={statusOptions}
              placeholder="Status"
              label="Status"
            />
          </div>

          <div className="w-40">
            <FilterSelect
              value={read}
              onValueChange={onReadChange}
              options={readOptions}
              placeholder="Read Status"
              label="Read Status"
            />
          </div>

          <Button
            variant="outline"
            onClick={onReset}
            className="flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Reset</span>
          </Button>
        {/* </div> */}
      </div>
    </div>
  );
};
